import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Card } from "../../components/Card";
import { PageHeader } from "../../components/ui/PageHeader";
import {
  useTemplate,
  useCreateTemplate,
  useUpdateTemplate,
  useDeleteTemplate,
} from "../../hooks/usePromptTemplates";
import type { TemplateCategory, TemplateVariable } from "../../api/promptTemplates";

// ─── Constants ──────────────────────────────────────────────────────────────────

const CATEGORIES: { value: TemplateCategory; label: string }[] = [
  { value: "credential", label: "Credential" },
  { value: "pii", label: "PII" },
  { value: "access", label: "Access" },
  { value: "financial", label: "Financial" },
  { value: "custom", label: "Custom" },
];

// ─── Helpers ────────────────────────────────────────────────────────────────────

function FieldLabel({ children }: { children: string }) {
  return (
    <label
      style={{
        fontSize: 9.5,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        color: "var(--text-muted)",
        fontWeight: 600,
      }}
    >
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  background: "var(--bg-input)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  padding: "7px 10px",
  color: "var(--text-primary)",
  fontSize: 12,
  fontFamily: "var(--font-sans)",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

// ─── Variable row type (local mutable form state) ───────────────────────────────

interface VarRow {
  key: string;
  label: string;
  placeholder: string;
  auto_resolve: string;
}

function emptyVar(): VarRow {
  return { key: "", label: "", placeholder: "", auto_resolve: "" };
}

function toApiVars(rows: VarRow[]): TemplateVariable[] {
  return rows
    .filter((r) => r.key.trim() !== "")
    .map((r) => ({
      key: r.key.trim(),
      label: r.label.trim() || r.key.trim(),
      placeholder: r.placeholder.trim(),
      auto_resolve: r.auto_resolve.trim() || null,
    }));
}

function fromApiVars(vars: TemplateVariable[]): VarRow[] {
  return vars.map((v) => ({
    key: v.key,
    label: v.label,
    placeholder: v.placeholder,
    auto_resolve: v.auto_resolve ?? "",
  }));
}

// ─── Component ───────────────────────────────────────────────────────────────────

export function TemplateEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === "new";

  const { data, isLoading, isError } = useTemplate(isNew ? "" : id);
  const createMutation = useCreateTemplate();
  const updateMutation = useUpdateTemplate(isNew ? "" : (id ?? ""));
  const deleteMutation = useDeleteTemplate();

  // ── Local form state ──────────────────────────────────────────────────────────

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<TemplateCategory>("custom");
  const [suggestedRole, setSuggestedRole] = useState("");
  const [promptBody, setPromptBody] = useState("");
  const [variables, setVariables] = useState<VarRow[]>([emptyVar()]);
  const [seeded, setSeeded] = useState(false);

  // Seed form from loaded record
  useEffect(() => {
    if (!isNew && data?.record && !seeded) {
      const rec = data.record;
      setName(rec.name);
      setDescription(rec.description);
      setCategory(rec.category);
      setSuggestedRole(rec.suggested_role);
      setPromptBody(rec.prompt_body);
      setVariables(
        rec.variables.length > 0 ? fromApiVars(rec.variables) : [emptyVar()],
      );
      setSeeded(true);
    }
  }, [isNew, data, seeded]);

  // ── Loading/error states ──────────────────────────────────────────────────────

  if (!isNew && isLoading) {
    return (
      <div style={{ padding: 24, color: "var(--text-muted)", fontFamily: "var(--font-sans)" }}>
        Loading template...
      </div>
    );
  }

  if (!isNew && (isError || (!isLoading && !data))) {
    return (
      <div style={{ padding: 24, color: "var(--error)", fontFamily: "var(--font-sans)" }}>
        Failed to load template.{" "}
        <button
          onClick={() => navigate("/templates")}
          style={{ color: "var(--accent)", background: "none", border: "none", cursor: "pointer" }}
        >
          Back to templates
        </button>
      </div>
    );
  }

  // ── Save handlers ─────────────────────────────────────────────────────────────

  function handleCreate() {
    createMutation.mutate(
      {
        name: name.trim(),
        description: description.trim(),
        category,
        suggested_role: suggestedRole.trim(),
        prompt_body: promptBody,
        variables: toApiVars(variables),
      },
      {
        onSuccess: (resp) => {
          navigate(`/templates/${resp.record.template_id}`);
        },
      },
    );
  }

  function saveField(field: string, value: unknown) {
    if (isNew) return;
    updateMutation.mutate({ [field]: value });
  }

  function handleDelete() {
    if (!id || isNew) return;
    if (!window.confirm("Delete this template? This cannot be undone.")) return;
    deleteMutation.mutate(id, {
      onSuccess: () => navigate("/templates"),
    });
  }

  // ── Variable list helpers ─────────────────────────────────────────────────────

  function updateVar(index: number, field: keyof VarRow, value: string) {
    setVariables((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  }

  function addVar() {
    setVariables((prev) => [...prev, emptyVar()]);
  }

  function removeVar(index: number) {
    setVariables((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length === 0 ? [emptyVar()] : next;
    });
  }

  function saveVariables() {
    if (isNew) return;
    saveField("variables", toApiVars(variables));
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div style={{ fontFamily: "var(--font-sans)", maxWidth: 800 }}>
      {/* Breadcrumb */}
      <div
        style={{
          fontSize: 11,
          color: "var(--text-muted)",
          fontFamily: "var(--font-mono)",
          marginBottom: 12,
        }}
      >
        <Link
          to="/templates"
          style={{ color: "var(--text-muted)", textDecoration: "none" }}
        >
          Templates
        </Link>
        <span style={{ padding: "0 6px" }}>/</span>
        <span style={{ color: "var(--text-primary)", fontWeight: 700 }}>
          {isNew ? "New template" : (name || "...")}
        </span>
      </div>

      <PageHeader
        eyebrow="Template editor"
        title={isNew ? "New template" : name || "..."}
        subtitle={
          isNew
            ? "Fill in the template details and click Create."
            : "Edit fields below. Changes save on blur."
        }
        actions={
          isNew ? (
            <button
              onClick={handleCreate}
              disabled={!name.trim() || createMutation.isPending}
              style={{
                padding: "7px 18px",
                borderRadius: 8,
                fontSize: 11,
                fontWeight: 600,
                border: "1px solid var(--accent)",
                background: !name.trim() ? "var(--bg-tertiary)" : "var(--accent)",
                color: !name.trim() ? "var(--text-muted)" : "white",
                cursor: !name.trim() ? "not-allowed" : "pointer",
              }}
            >
              {createMutation.isPending ? "Creating..." : "Create"}
            </button>
          ) : (
            <button
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              style={{
                padding: "7px 14px",
                borderRadius: 8,
                fontSize: 11,
                fontWeight: 600,
                border: "1px solid rgba(239,68,68,0.5)",
                background: "rgba(239,68,68,0.12)",
                color: "var(--error)",
                cursor: "pointer",
              }}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete template"}
            </button>
          )
        }
      />

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {/* Basic info card */}
        <Card title="Template details">
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <FieldLabel>Name</FieldLabel>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => saveField("name", name.trim())}
              placeholder="e.g. IT Help Desk"
              style={inputStyle}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <FieldLabel>Description</FieldLabel>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={() => saveField("description", description.trim())}
              placeholder="Brief description of this template's purpose"
              style={inputStyle}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <FieldLabel>Category</FieldLabel>
              <select
                value={category}
                onChange={(e) => {
                  const val = e.target.value as TemplateCategory;
                  setCategory(val);
                  saveField("category", val);
                }}
                style={{
                  ...inputStyle,
                  cursor: "pointer",
                }}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <FieldLabel>Suggested role</FieldLabel>
              <input
                value={suggestedRole}
                onChange={(e) => setSuggestedRole(e.target.value)}
                onBlur={() => saveField("suggested_role", suggestedRole.trim())}
                placeholder="e.g. IT Help Desk Analyst"
                style={inputStyle}
              />
            </div>
          </div>
        </Card>

        {/* Prompt body card */}
        <Card title="Prompt body">
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <FieldLabel>System prompt template</FieldLabel>
            <textarea
              value={promptBody}
              onChange={(e) => setPromptBody(e.target.value)}
              onBlur={() => saveField("prompt_body", promptBody)}
              rows={12}
              placeholder={"Use {variable_name} for placeholders.\n\nExample:\nYou are {persona_name} from {company_name}..."}
              style={{
                ...inputStyle,
                resize: "vertical",
                fontFamily: "var(--font-mono)",
                fontSize: 11.5,
                lineHeight: 1.6,
              }}
            />
          </div>
          <div style={{ fontSize: 10, color: "var(--text-muted)", fontStyle: "italic" }}>
            Variables like {"{persona_name}"} will be replaced when the template is applied to a persona.
          </div>
        </Card>

        {/* Variables card */}
        <Card title="Variables">
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* Header row */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr 32px",
                gap: 8,
                fontSize: 9.5,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.6,
                color: "var(--text-muted)",
              }}
            >
              <span>Key</span>
              <span>Label</span>
              <span>Placeholder</span>
              <span>Auto-resolve</span>
              <span />
            </div>

            {/* Variable rows */}
            {variables.map((v, i) => (
              <div
                key={i}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr 1fr 32px",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                <input
                  value={v.key}
                  onChange={(e) => updateVar(i, "key", e.target.value)}
                  onBlur={saveVariables}
                  placeholder="variable_key"
                  style={{ ...inputStyle, fontFamily: "var(--font-mono)", fontSize: 11 }}
                />
                <input
                  value={v.label}
                  onChange={(e) => updateVar(i, "label", e.target.value)}
                  onBlur={saveVariables}
                  placeholder="Display label"
                  style={inputStyle}
                />
                <input
                  value={v.placeholder}
                  onChange={(e) => updateVar(i, "placeholder", e.target.value)}
                  onBlur={saveVariables}
                  placeholder="e.g. Acme Corp"
                  style={inputStyle}
                />
                <input
                  value={v.auto_resolve}
                  onChange={(e) => updateVar(i, "auto_resolve", e.target.value)}
                  onBlur={saveVariables}
                  placeholder="persona.display_name"
                  style={{ ...inputStyle, fontFamily: "var(--font-mono)", fontSize: 11 }}
                />
                <button
                  onClick={() => {
                    removeVar(i);
                    if (!isNew) {
                      // Save after removing — use timeout to let state update
                      setTimeout(() => {
                        const next = variables.filter((_, idx) => idx !== i);
                        const cleaned = next.length === 0 ? [] : toApiVars(next);
                        saveField("variables", cleaned);
                      }, 0);
                    }
                  }}
                  title="Remove variable"
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    border: "1px solid var(--border)",
                    background: "transparent",
                    color: "var(--text-muted)",
                    cursor: "pointer",
                    fontSize: 14,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  x
                </button>
              </div>
            ))}

            {/* Add variable button */}
            <button
              onClick={addVar}
              style={{
                alignSelf: "flex-start",
                padding: "5px 12px",
                borderRadius: 6,
                fontSize: 10.5,
                fontWeight: 600,
                border: "1px solid var(--border)",
                background: "var(--bg-tertiary)",
                color: "var(--text-secondary)",
                cursor: "pointer",
              }}
            >
              + Add variable
            </button>
          </div>
          <div style={{ fontSize: 10, color: "var(--text-muted)", fontStyle: "italic" }}>
            Auto-resolve values (e.g. <code>persona.display_name</code>) are filled automatically when
            applying the template to a persona.
          </div>
        </Card>
      </div>
    </div>
  );
}
